/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";
import {
  DisplayRef,
  DisplayRef$inboundSchema,
  DisplayRef$Outbound,
  DisplayRef$outboundSchema,
} from "./displayref.js";

/**
 * The sequencer configuration, it's limited because it appears on the Details Page of another Device.
 */
export type SequencerConfigState = {
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  conditionExpression?: ConditionExpression | undefined;
  /**
   * The alias of the sequencer device.
   */
  deviceAlias?: string | undefined;
  /**
   * The id of the sequencer device.
   */
  deviceId?: string | undefined;
  /**
   * The readings to display, this displays readings in the Sequencer section of the Equipment Details Screen.
   */
  displayMetrics: Array<DisplayRef>;
  /**
   * The name of the sequencer config. Usually used for debug purposes.
   */
  displayName?: string | undefined;
  /**
   * The settings to display, this displays settings in the Sequencer section of the Equipment Details Screen.
   */
  displaySettings: Array<DisplayRef>;
  /**
   * Whether this state is the default state. If this is true, conditions are not required. For readability, these states should be at the end of the array.
   */
  isDefault?: boolean | undefined;
  /**
   * The priority of the state. There is no constraint on overlapping conditions, so this field allows user to set priority.
   */
  priority?: number | undefined;
  /**
   * The name of the state
   */
  stateName: string;
};

/** @internal */
export const SequencerConfigState$inboundSchema: z.ZodType<
  SequencerConfigState,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition_expression: types.optional(ConditionExpression$inboundSchema),
  device_alias: types.optional(types.string()),
  device_id: types.optional(types.string()),
  display_metrics: z.array(DisplayRef$inboundSchema),
  display_name: types.optional(types.string()),
  display_settings: z.array(DisplayRef$inboundSchema),
  is_default: types.boolean().default(false),
  priority: types.number().default(0),
  state_name: types.string(),
}).transform((v) => {
  return remap$(v, {
    "condition_expression": "conditionExpression",
    "device_alias": "deviceAlias",
    "device_id": "deviceId",
    "display_metrics": "displayMetrics",
    "display_name": "displayName",
    "display_settings": "displaySettings",
    "is_default": "isDefault",
    "state_name": "stateName",
  });
});
/** @internal */
export type SequencerConfigState$Outbound = {
  condition_expression?: ConditionExpression$Outbound | undefined;
  device_alias?: string | undefined;
  device_id?: string | undefined;
  display_metrics: Array<DisplayRef$Outbound>;
  display_name?: string | undefined;
  display_settings: Array<DisplayRef$Outbound>;
  is_default: boolean;
  priority: number;
  state_name: string;
};

/** @internal */
export const SequencerConfigState$outboundSchema: z.ZodType<
  SequencerConfigState$Outbound,
  z.ZodTypeDef,
  SequencerConfigState
> = z.object({
  conditionExpression: ConditionExpression$outboundSchema.optional(),
  deviceAlias: z.string().optional(),
  deviceId: z.string().optional(),
  displayMetrics: z.array(DisplayRef$outboundSchema),
  displayName: z.string().optional(),
  displaySettings: z.array(DisplayRef$outboundSchema),
  isDefault: z.boolean().default(false),
  priority: z.number().int().default(0),
  stateName: z.string(),
}).transform((v) => {
  return remap$(v, {
    conditionExpression: "condition_expression",
    deviceAlias: "device_alias",
    deviceId: "device_id",
    displayMetrics: "display_metrics",
    displayName: "display_name",
    displaySettings: "display_settings",
    isDefault: "is_default",
    stateName: "state_name",
  });
});

export function sequencerConfigStateToJSON(
  sequencerConfigState: SequencerConfigState,
): string {
  return JSON.stringify(
    SequencerConfigState$outboundSchema.parse(sequencerConfigState),
  );
}
export function sequencerConfigStateFromJSON(
  jsonString: string,
): SafeParseResult<SequencerConfigState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SequencerConfigState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SequencerConfigState' from JSON`,
  );
}
